import React, { useEffect, useMemo, useState } from "react";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import AppLoader from "../../../components/Loader/AppLoader";
import { useListSchemeAssignedUsersMutation } from "../../../features/tenant/Aqualite/aqualiteApi";

const ViewSchemeUsersModal = ({ show, setShow, scheme }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);
  const [mobileInput, setMobileInput] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");

  const tenant_id = JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;
  const token = sessionStorage.getItem("access_token");

  const [listAssignedUsers, { isLoading }] = useListSchemeAssignedUsersMutation();

  const fetchUsers = async (pageNum = 1, pageLimit = limit, mobile = mobileFilter) => {
    if (!scheme?.id) return;

    try {
      const body = {
        scheme_id: scheme.id,
        page: pageNum,
        limit: pageLimit,
      };

      if (mobile?.trim()) {
        body.mobile = mobile.trim();
      }

      const response = await listAssignedUsers({
        token,
        tenant_id,
        body,
      }).unwrap();

      if (response?.status) {
        setUsers(response.body?.users || []);
        setTotal(response.body?.total || 0);
        setPage(response.body?.page || pageNum);
      } else {
        setUsers([]);
        setTotal(0);
        setPage(1);
      }
    } catch {
      setUsers([]);
      setTotal(0);
      setPage(1);
    }
  };

  useEffect(() => {
    if (!show) {
      setUsers([]);
      setTotal(0);
      setPage(1);
      setLimit(10);
      setMobileInput("");
      setMobileFilter("");
      return;
    }
    setPage(1);
    setLimit(10);
    setMobileInput("");
    setMobileFilter("");
    setPaginationResetToggle((prev) => !prev);
    fetchUsers(1, 10, "");
  }, [show, scheme?.id]);

  const handleSearch = () => {
    setPage(1);
    setMobileFilter(mobileInput.trim());
    setPaginationResetToggle((prev) => !prev);
    fetchUsers(1, limit, mobileInput.trim());
  };

  const handleClearFilter = () => {
    setMobileInput("");
    setMobileFilter("");
    setPage(1);
    setPaginationResetToggle((prev) => !prev);
    fetchUsers(1, limit, "");
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    fetchUsers(pageNumber, limit, mobileFilter);
  };

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setPage(1);
    setPaginationResetToggle((prev) => !prev);
    fetchUsers(1, newPerPage, mobileFilter);
  };

  const columns = useMemo(
    () => [
      {
        name: "SNo",
        selector: (row, idx) => (page - 1) * limit + Number(idx) + 1,
        width: "80px",
      },
      {
        name: "Name",
        selector: (row) => row.name,
        sortable: true,
      },
      {
        name: "Mobile",
        selector: (row) => row.mobile,
        sortable: true,
      },
      {
        name: "Type",
        selector: (row) => row.user_type || "-",
        sortable: true,
      },
      {
        name: "State",
        selector: (row) => row.state || "-",
        sortable: true,
      },
    ],
    [page, limit],
  );

  return (
    <ModalComponent show={show} setShow={setShow} size="4xl">
      <div className="p-4 min-h-[50vh]">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          Assigned Users - {scheme?.name || ""}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Scheme ID: {scheme?.id} | Total users: {isLoading && !users.length ? "..." : total}
        </p>

        <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3 mb-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="w-52">
              <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                Mobile
              </label>
              <input
                type="search"
                className="w-full px-2 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                placeholder="Mobile number..."
                value={mobileInput}
                onChange={(e) => setMobileInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <button
              type="button"
              className="btn btn-outline-primary py-2"
              onClick={handleSearch}
            >
              Search
            </button>
            <button
              type="button"
              className="btn btn-outline-primary py-2 whitespace-nowrap"
              onClick={handleClearFilter}
            >
              Clear Filter
            </button>
          </div>
        </div>

        {isLoading && users.length === 0 ? (
          <div className="min-h-[40vh] flex items-center justify-center">
            <AppLoader text="Loading users..." size="sm" />
          </div>
        ) : (
          <DataTableComponent
            keyField={(row) => row.id}
            columns={columns}
            data={users}
            loading={isLoading}
            totalRows={total}
            handlePerRowsChange={handlePerRowsChange}
            handlePageChange={handlePageChange}
            paginationDefaultPage={page}
            paginationPerPage={limit}
            paginationResetDefaultPage={paginationResetToggle}
          />
        )}
      </div>
    </ModalComponent>
  );
};

export default ViewSchemeUsersModal;
